import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy, ViewEncapsulation } from '@angular/core';

/**
 * Button variants based on design system:
 * - primary: Blue filled button (main actions)
 * - secondary: Light blue tint bg + blue border (cancel, back, secondary actions)
 * - ghost: Transparent bg + gray border (subtle actions, e.g., hotel selection modal)
 * - danger: Red button (destructive actions)
 * - destructive-secondary: Red outlined button
 * - link: Text only, no background (inline actions)
 */
export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'destructive-secondary' | 'link';
export type ButtonSize = 'sm' | 'md' | 'lg';
export type ButtonType = 'button' | 'submit' | 'reset';

/**
 * Button Component
 *
 * A reusable button component with consistent styling across the application.
 * Icon is always positioned on the right side of the text.
 *
 * @example
 * ```html
 * <!-- Primary button -->
 * <app-button variant="primary" (clicked)="save()">Save</app-button>
 *
 * <!-- With icon (appears on right) -->
 * <app-button variant="primary" icon="plus">Add</app-button>
 *
 * <!-- Loading state -->
 * <app-button variant="primary" [loading]="isSaving">Save</app-button>
 *
 * <!-- Full width -->
 * <app-button variant="primary" [fullWidth]="true">Submit</app-button>
 *
 * <!-- Icon-only button (requires ariaLabel) -->
 * <hc-button variant="secondary" [iconOnly]="true" rightIcon="edit-2" ariaLabel="Edit item"></hc-button>
 * ```
 */
@Component({
  selector: 'hc-button',
  standalone: false,
  templateUrl: './button.component.html',
  styleUrl: './button.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class ButtonComponent {
  /** Button variant */
  @Input() variant: ButtonVariant = 'primary';

  /** Button size — defaults to sm (Figma "Small"). Use md only for primary form submit buttons. */
  @Input() size: ButtonSize = 'sm';

  /** Button type attribute */
  @Input() type: ButtonType = 'button';

  /** Whether the button is disabled */
  @Input() disabled = false;

  /** Whether the button is in loading state */
  @Input() loading = false;

  /** Whether this is an icon-only button */
  @Input() iconOnly = false;

  /** Right-side icon name (Lucide) */
  @Input() rightIcon = '';

  /** Left-side icon name (Lucide) — maps to Figma dIspLeftIcon */
  @Input() leftIcon = '';

  /** Destructive mood for link variant — maps to Figma Button/Link mood: Destructive */
  @Input() destructive = false;

  /** Make button full width of container */
  @Input() fullWidth = false;

  /** Make button square (equal width/height, for icon buttons) */
  @Input() square = false;

  /** Aria label (required for icon-only buttons) */
  @Input() ariaLabel = '';

  /** Click event emitter */
  @Output() clicked = new EventEmitter<MouseEvent>();

  /** Get the combined CSS classes for the button */
  get buttonClass(): string {
    const classes: string[] = ['hc-btn', `hc-btn-${this.variant}`];

    if (this.size !== 'md') {
      classes.push(`hc-btn-${this.size}`);
    }

    if (this.iconOnly) {
      classes.push('hc-btn-icon-only');
    }

    if (this.square) {
      classes.push('hc-btn-square');
    }

    if (this.fullWidth) {
      classes.push('hc-btn-block');
    }

    if (this.destructive && this.variant === 'link') {
      classes.push('hc-btn-link--destructive');
    }

    return classes.join(' ');
  }

  /** Handle click event */
  onClick(event: MouseEvent): void {
    if (!this.disabled && !this.loading) {
      this.clicked.emit(event);
    }
  }
}
